import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, ComboboxGroup, Input, Tooltip } from '@node-space/ui-components'
import { CountryCodes, type ErrorDetails } from 'types/types'
import { BUSINESS_CUSTOMER_DEFAULT_FORM_DATA } from '../helpers/form'
import { BusinessCustomerFormData, ConfirmBusinessCustomerMethod } from '../helpers/types'
import { scrollToTopError } from '../helpers/utils'
import { useBusinessCustomerForm } from '../hooks/useBusinessCustomerForm'
import { AddCustomerActions } from './AddCustomerActions'

export interface AddBusinessCustomerProps {
  defaultValues: BusinessCustomerFormData
  onConfirm: ConfirmBusinessCustomerMethod
  onCancel: () => void
  fieldErrors?: ErrorDetails
  isReliance: boolean
}

export const AddBusinessCustomer = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
  isReliance,
}: AddBusinessCustomerProps) => {
  const { t } = useTranslation()

  const { formFields, displayData, hasErrors, handleSubmit, form, formValues } =
    useBusinessCustomerForm(
      defaultValues ?? BUSINESS_CUSTOMER_DEFAULT_FORM_DATA,
      fieldErrors,
      isReliance
    )
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const onInvalid = () => {
    scrollToTopError(form, fieldRefs)
  }

  const showUsTaxNumberField = useMemo(() => {
    return isReliance && formValues?.countryOfIncorporation === CountryCodes.US
  }, [isReliance, formValues?.countryOfIncorporation])

  const relianceFields = () => {
    if (!isReliance) return null
    if (showUsTaxNumberField) {
      return (
        <Box ref={el => (fieldRefs.current.taxNumber = el)}>
          <Input {...formFields?.taxNumber} />
        </Box>
      )
    }
    return (
      <Box ref={el => (fieldRefs.current.registrationNumber = el)}>
        <Input {...formFields?.registrationNumber} />
      </Box>
    )
  }

  useEffect(() => {
    if (!hasErrors) return
    scrollToTopError(form, fieldRefs)
  }, [hasErrors, form?.formState?.errors])

  const onSubmit = (data: BusinessCustomerFormData) => {
    onConfirm({ data, displayData })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Box flexCol gap={16}>
        <Box ref={el => (fieldRefs.current.businessName = el)}>
          <Input
            {...formFields?.businessName}
            placeholder={t('embeddedCustomers.businessFormPlaceholder.businessName')}
            testId="business-name"
          />
        </Box>
        <Box ref={el => (fieldRefs.current.businessDescription = el)}>
          <Input
            {...formFields?.businessDescription}
            testId="business-description"
            Tooltip={
              <Tooltip
                type="complex"
                bodyContent={t('embeddedCustomers.businessFormTooltip.businessDescription')}
              />
            }
          />
        </Box>
        <Box ref={el => (fieldRefs.current.industry = el)}>
          <ComboboxGroup {...formFields?.industry} />
        </Box>
        <Box ref={el => (fieldRefs.current.countryOfIncorporation = el)}>
          <Combobox {...formFields?.countryOfIncorporation} />
        </Box>
        <Box ref={el => (fieldRefs.current.monthlyExpectedVolumes = el)}>
          <Combobox {...formFields?.monthlyExpectedVolumes} />
        </Box>
        <Box ref={el => (fieldRefs.current.useCase = el)}>
          <Combobox
            {...formFields?.useCase}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.serviceTypeTooltip')} />}
          />
        </Box>
        <Box ref={el => (fieldRefs.current.externalReference = el)}>
          <Input
            {...formFields?.externalReference}
            testId="business-external-reference"
            optionalText={t('optional')}
            Tooltip={
              <Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />
            }
          />
        </Box>
        {relianceFields()}
      </Box>
      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
